import { makeAutoObservable, runInAction } from 'mobx';
import professionalModel from "../../../model/professional/professional-model";
import companyCollaboratorsViewModel from "./company-collaborators-view-model";
import companyStore from "../../../store/company-store";

export interface Permission {
  id: string;
  name: string;
}

export interface Role {
  id: string;
  name: string;
  description?: string;
}

export interface CollaboratorData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  picture: string | null;
}

export interface CollaboratorViewData {
  collaborator: CollaboratorData;
  role: Role;
  collaboratorCompanyId: string;
  permissions: Permission[];
}

export class CompanyCollaboratorDetailViewModel {

  data: CollaboratorViewData | null = null;
  isLoading: boolean = false;
  error: string | null = null;

  vmCompanyCollaboratorsViewModel = companyCollaboratorsViewModel;

  private static instance: CompanyCollaboratorDetailViewModel;

  private constructor() {
    makeAutoObservable(this);
  }

  public static getInstance(): CompanyCollaboratorDetailViewModel {
    if (!CompanyCollaboratorDetailViewModel.instance) {
      CompanyCollaboratorDetailViewModel.instance = new CompanyCollaboratorDetailViewModel();
    }
    return CompanyCollaboratorDetailViewModel.instance;
  }

  setData(data: CollaboratorViewData) {
    this.data = data;
  }

  async loadCollaboratorData() {
    this.isLoading = true;
    this.error = null;

    try {
      const company = companyStore.getCompanyById;
      if (!company) return;

      const response = await professionalModel.handleGetDataCollaboratorByCompany(
        company.id,
        this.vmCompanyCollaboratorsViewModel.selectedCollaborator.collaboratorId
      );

      runInAction(() => {
        this.setData(response);
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = "Erreur lors du chargement des données du collaborateur";
        this.isLoading = false;
      });
      console.error("Erreur API loadCollaboratorData:", error);
      throw error;
    }
  }

  get groupedPermissions(): Record<string, string[]> {
    const groupedPermissions: Record<string, string[]> = {};

    if (!this.data) {
      return groupedPermissions;
    }

    const uniquePermissions = this.data.permissions.filter(
      (permission, index, self) =>
        index === self.findIndex(p => p.id === permission.id)
    );

    uniquePermissions.forEach(permission => {
      const parts = permission.name.split(' ');
      if (parts.length > 0) {
        const action = parts[0];
        const resource = parts.slice(1).join(' ');

        if (!groupedPermissions[action]) {
          groupedPermissions[action] = [];
        }
        groupedPermissions[action].push(resource);
      }
    });

    return groupedPermissions;
  }

  getRoleBadgeClass = (roleName: string): string => {
    switch (roleName) {
      case 'OWNER':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      case 'ADMIN':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'STAFF':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  getFormattedName = (firstName: string, lastName: string): string => {
    return `${firstName.charAt(0).toUpperCase() + firstName.slice(1)} ${lastName.charAt(0).toUpperCase() + lastName.slice(1)}`;
  };

  getInitials = (firstName: string, lastName: string): string => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };
}

export default CompanyCollaboratorDetailViewModel.getInstance();
